/**
 * The day-then-game picker the Strip Card create step and Add a game share
 * (ISSUE-1287, ISSUE-1292), after v1's LeagueDatePicker and
 * MatchesPerDateDropdown. Days come from `/event/days-with-games-list` for
 * the league, from today on, in the fan's own zone; games from
 * `/event/list` for the picked day. A game whose kickoff has passed is not
 * offered (the server's `isKickoffPassed`), and contest-api checks again on
 * save.
 */
import {
  useEventDaysWithGamesList,
  useEventList,
} from "@sportsmet/events-sdk/private";
import { Radio, Select, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { longDayLabel, shiftDay, todayInZone } from "../scores/calendarDay";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  STRIP_PICKER_DAY_LABEL,
  STRIP_PICKER_DAY_PLACEHOLDER,
  STRIP_PICKER_GAME_LABEL,
  STRIP_PICKER_LOADING,
  STRIP_PICKER_NO_DAYS,
  STRIP_PICKER_NO_GAMES,
  stripGameHeading,
} from "./stripCardCopy";
import { stripKickoffLabel } from "./stripTime";

/** events-api answers at most 62 days per read. */
export const STRIP_PICKER_WINDOW_DAYS = 60;

export type typeStripGamePick = {
  day: string | null;
  eventId: string | null;
  /**
   * The picked game as the fan saw it ("Bears at Packers, Sun, Nov 15, 1:00
   * PM"), so a later step can repeat it without a second read. Null until a
   * game is picked.
   */
  gameLabel?: string | null;
};

export type typeStripGamePickerProps = {
  leagueId: string;
  value: typeStripGamePick;
  onChange: (next: typeStripGamePick) => void;
  /** Games already on the pool, shown but not pickable. */
  takenEventIds?: readonly string[];
  takenHint?: string;
};

export const StripGamePicker = (props: typeStripGamePickerProps): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const today = todayInZone({ nowMs: Date.now(), timeZone });
  const daysRead = useEventDaysWithGamesList({
    leagueId: props.leagueId,
    fromDay: today,
    toDay: shiftDay({ day: today, deltaDays: STRIP_PICKER_WINDOW_DAYS }),
    timeZone,
  });
  const days = daysRead.data?.data?.days ?? [];
  const day = props.value.day;
  const gamesRead = useEventList(
    { leagueId: props.leagueId, day: day ?? "", timeZone },
    { enabled: day !== null },
  );
  const games = (gamesRead.data?.data?.events ?? []).filter(
    (event) => !event.isKickoffPassed,
  );
  const taken = new Set(props.takenEventIds ?? []);

  if (daysRead.isPending) {
    return (
      <div
        className="flex flex-col gap-2"
        aria-label={STRIP_PICKER_LOADING}
        data-testid="strip-game-picker-loading"
      >
        <Skeleton shape="card" />
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <p
        className="text-body text-ink-secondary"
        data-testid="strip-game-picker-no-days"
      >
        {STRIP_PICKER_NO_DAYS}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="strip-game-picker">
      <Select
        label={STRIP_PICKER_DAY_LABEL}
        placeholder={STRIP_PICKER_DAY_PLACEHOLDER}
        value={day ?? undefined}
        items={days.map((candidate) => ({
          value: candidate,
          label: longDayLabel(candidate, today),
        }))}
        onValueChange={(next) => {
          props.onChange({ day: next, eventId: null, gameLabel: null });
        }}
      />
      {day === null ? null : gamesRead.isPending ? (
        <Skeleton shape="card" />
      ) : games.length === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="strip-game-picker-no-games"
        >
          {STRIP_PICKER_NO_GAMES}
        </p>
      ) : (
        <Radio
          label={STRIP_PICKER_GAME_LABEL}
          value={props.value.eventId ?? ""}
          onValueChange={(eventId) => {
            const event = games.find(
              (candidate) => candidate.eventId === eventId,
            );
            props.onChange({
              day,
              eventId,
              gameLabel:
                event === undefined
                  ? null
                  : `${stripGameHeading({
                      awayTeamName: event.away.name,
                      homeTeamName: event.home.name,
                    })}, ${stripKickoffLabel({
                      kickoffAt: event.kickoffAt,
                      timeZone,
                    })}`,
            });
          }}
          items={games.map((event) => {
            const isTaken = taken.has(event.eventId);
            const kickoff = stripKickoffLabel({
              kickoffAt: event.kickoffAt,
              timeZone,
            });
            return {
              value: event.eventId,
              label: stripGameHeading({
                awayTeamName: event.away.name,
                homeTeamName: event.home.name,
              }),
              description:
                isTaken && props.takenHint !== undefined
                  ? `${kickoff}. ${props.takenHint}`
                  : kickoff,
              disabled: isTaken,
            };
          })}
        />
      )}
    </div>
  );
};
